import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { updateUserMeProfile } from '@/data/mutations'
import { getIdTokenUsingQueryClient, queryKeys } from '@/data/queries'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import {
  createFileRoute,
  redirect,
  useRouteContext,
} from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import * as toast from '@/components/ui/toast'
import { LoadingSettings } from '@/components/loading-ui/LoadingSettings'
import * as Sentry from '@sentry/react'
import { useSelectedTenantStore } from '@/stores/useSelectedTenantStore'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { userMeProfileOptions, userMeOptions } from '@/data/queryOptions'
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  multiFactor,
  TotpMultiFactorGenerator,
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { Separator } from '@/components/ui/separator'
import { TwoFactorSetupDialog } from '@/components/dashboard/settings-page/TwoFactorSetupDialog'
import { TwoFactorDisableDialog } from '@/components/dashboard/settings-page/TwoFactorDisableDialog'
import { WebAuthnEnrollDialog } from '@/components/dashboard/settings-page/WebAuthnEnrollDialog'
import { WebAuthnCredentialList } from '@/components/dashboard/settings-page/WebAuthnCredentialList'
import { MfaResetByCodeDialog } from '@/components/dashboard/settings-page/MfaResetByCodeDialog'
import { Badge } from '@/components/ui/badge'
import { useState } from 'react'
import { useSignOut } from '@/hooks/useSignOut'
import { ShieldAlert } from 'lucide-react'

const formSchema = z.object({
  displayName: z.string().min(1, { message: 'Name cannot be empty.' }),
})

const passwordFormSchema = z
  .object({
    currentPassword: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters.' }),
    newPassword: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters.' }),
    confirmPassword: z
      .string()
      .min(6, { message: 'Password must be at least 6 characters.' }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

export const Route = createFileRoute(
  '/_authenticated/_settings/user/$userId/my-profile',
)({
  component: RouteComponent,
  loader: async ({ context }) => {
    const { queryClient, authState } = context

    const TENANT_ID = useSelectedTenantStore.getState().selectedTenantId
    if (!TENANT_ID) {
      throw redirect({ to: '/select-tenant' })
    }
    const idToken = await getIdTokenUsingQueryClient({
      queryClient,
      authState: authState!,
    })

    return Promise.all([
      queryClient.ensureQueryData(userMeOptions(TENANT_ID, idToken!)),
      queryClient.ensureQueryData(userMeProfileOptions(TENANT_ID, idToken!)),
    ])
  },
  pendingComponent: LoadingSettings,
})

function RouteComponent() {
  const isMobile = useIsMobile()
  const { authState } = useRouteContext({ from: '/_authenticated' })
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()

  const { data: user } = useSuspenseQuery(userMeOptions(TENANT_ID!, idToken!))
  const { data: userById } = useSuspenseQuery(
    userMeProfileOptions(TENANT_ID!, idToken!),
  )

  const queryClient = useQueryClient()
  const handleSignOut = useSignOut()
  const [setupDialogOpen, setSetupDialogOpen] = useState(false)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const [passkeyEnrollOpen, setPasskeyEnrollOpen] = useState(false)
  const [mfaResetOpen, setMfaResetOpen] = useState(false)

  const hasPasswordProvider = auth.currentUser?.providerData.some(
    (p) => p.providerId === 'password',
  )
  const hasTOTP = auth.currentUser
    ? multiFactor(auth.currentUser).enrolledFactors.some(
        (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID,
      )
    : false

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      displayName: userById?.name ?? user.name ?? '',
    },
  })

  const passwordForm = useForm<z.infer<typeof passwordFormSchema>>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const saveUserProfileMutation = useMutation({
    mutationFn: (data: z.infer<typeof formSchema>) => {
      return updateUserMeProfile({
        authState: authState!,
        tenantId: TENANT_ID!,
        data,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.all(TENANT_ID!),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.meProfile(TENANT_ID!),
      })
      toast.success('Profile updated successfully')
    },
    onError: (error) => {
      Sentry.captureException(error)
      toast.error('Failed to update profile. Please try again.')
    },
  })

  const updatePasswordMutation = useMutation({
    mutationFn: async (data: z.infer<typeof passwordFormSchema>) => {
      const currentUser = auth.currentUser
      if (!currentUser || !currentUser.email) {
        throw new Error('User not authenticated')
      }

      const credential = EmailAuthProvider.credential(
        currentUser.email,
        data.currentPassword,
      )
      await reauthenticateWithCredential(currentUser, credential)
      await updatePassword(currentUser, data.newPassword)
    },
    onSuccess: () => {
      toast.success('Password updated successfully')
      passwordForm.reset()
    },
    onError: (error) => {
      Sentry.captureException(error)
      const errorCode = (error as any)?.code

      if (
        errorCode === 'auth/wrong-password' ||
        errorCode === 'auth/invalid-credential'
      ) {
        toast.error('Current password is incorrect')
      } else if (errorCode === 'auth/weak-password') {
        toast.error('New password is too weak')
      } else if (errorCode === 'auth/requires-recent-login') {
        toast.error(
          'Please log out and log in again before changing your password',
        )
      } else if (errorCode === 'auth/user-not-found') {
        toast.error('User not found. Please log out and log in again.')
      } else if (errorCode === 'auth/invalid-email') {
        toast.error('Invalid email address. Please log out and log in again.')
      } else if (errorCode === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection.')
      } else if (errorCode === 'auth/user-mismatch') {
        toast.error(
          'Authentication error. This might happen if you logged in with a social provider (Google/Microsoft). Password changes are only available for email/password accounts.',
        )
      } else {
        toast.error('Failed to update password. Please try again.')
      }
    },
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    saveUserProfileMutation.mutate(data)
  }

  const onPasswordSubmit = (data: z.infer<typeof passwordFormSchema>) => {
    updatePasswordMutation.mutate(data)
  }

  return (
    <div className="settings-container space-y-2">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <h1 className="text-2xl font-semibold">Basic Information</h1>

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. John Smith"
                      {...field}
                      className="max-w-lg"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* This is not in the form field because we're not allowing the user to change the email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="max-w-lg"
                aria-label="Email address"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={
              saveUserProfileMutation.isPending || !form.formState.isDirty
            }
            className={cn(isMobile && 'w-full max-w-lg')}
          >
            {saveUserProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </Form>
      {hasPasswordProvider && (
        <>
          <Separator />
          <Form {...passwordForm}>
            <form
              onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
              className="space-y-6"
            >
              <h2 className="text-xl font-semibold">Change Password</h2>

              <div className="space-y-4">
                <FormField
                  control={passwordForm.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your current password"
                          {...field}
                          className="max-w-lg"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your new password"
                          {...field}
                          className="max-w-lg"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm New Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Confirm your new password"
                          {...field}
                          className="max-w-lg"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                disabled={
                  updatePasswordMutation.isPending ||
                  !passwordForm.formState.isDirty
                }
                className={cn(isMobile && 'w-full max-w-lg')}
              >
                {updatePasswordMutation.isPending
                  ? 'Updating...'
                  : 'Update Password'}
              </Button>
            </form>
          </Form>
        </>
      )}
      <Separator />
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Two Factor Authentication</h2>
        <div className="flex max-w-lg items-center justify-between gap-2">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold">Authenticator app</p>
              {hasTOTP && <Badge variant="secondaryGreen">Enabled</Badge>}
            </div>
            <p className="text-muted-foreground text-sm">
              Use an authentication app or browser extension to get two-factor
              authentication codes when prompted.
            </p>
          </div>
          {hasTOTP ? (
            <Button
              variant="destructive"
              onClick={() => setDisableDialogOpen(true)}
            >
              Disable
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setSetupDialogOpen(true)}>
              Set Up
            </Button>
          )}
        </div>

        <Separator />

        {/* Passkeys subsection — sibling of the TOTP block above. */}
        <WebAuthnCredentialList
          onAddPasskey={() => setPasskeyEnrollOpen(true)}
        />

        <Separator />

        {/* Recovery: reset MFA via emailed code. Lives next to the MFA setup
            controls because that's where users will look when they have lost
            their authenticator. */}
        <div className="flex max-w-3xl items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="font-semibold">Lost access to your second factor?</p>
            <p className="text-muted-foreground text-sm">
              Receive a verification code by email to remove your TOTP
              authenticator and registered passkeys, then enroll new ones.
            </p>
          </div>
          <Button variant="outline" onClick={() => setMfaResetOpen(true)}>
            <ShieldAlert className="mr-2 h-4 w-4" />
            Reset MFA by email
          </Button>
        </div>
      </div>

      <TwoFactorSetupDialog
        open={setupDialogOpen}
        onOpenChange={setSetupDialogOpen}
      />
      <TwoFactorDisableDialog
        open={disableDialogOpen}
        onOpenChange={setDisableDialogOpen}
        onDisabled={() => {
          toast.success('Two-factor authentication disabled')
          window.location.reload()
        }}
      />
      <WebAuthnEnrollDialog
        open={passkeyEnrollOpen}
        onOpenChange={setPasskeyEnrollOpen}
        onEnrolled={() => {
          toast.success('Passkey added')
        }}
      />
      <MfaResetByCodeDialog
        open={mfaResetOpen}
        onOpenChange={setMfaResetOpen}
        onResetComplete={async () => {
          toast.success('MFA reset complete. Please sign in again.')
          await handleSignOut()
        }}
      />
    </div>
  )
}
